// src/hooks/useLastVisit.js
//
// Tracks a signed-in user's last Home visit — for EVERYONE, fan or
// creator, not just creators. This is the shared timestamp that
// powers two things:
//   - the fan-facing "new since you were last here" marker on Home
//   - the creator recap card's away-duration math (useCreatorRecap
//     takes previousVisitAt from here rather than tracking it itself,
//     so the two hooks never race to write the same field)
//
// Writes `lastHomeVisitAt` on the user's own doc once per Home
// mount, AFTER reading the prior value — so what's returned is
// always "when you were here before this visit," never "now."

import { useEffect, useState } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";

export default function useLastVisit(currentUser) {
  const [previousVisitAt, setPreviousVisitAt] = useState(null);
  const [loading, setLoading] = useState(Boolean(currentUser));

  useEffect(() => {
    if (!currentUser) {
      setPreviousVisitAt(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    async function run() {
      setLoading(true);
      try {
        const userRef = doc(db, "users", currentUser.uid);
        const snap = await getDoc(userRef);
        const prior = snap.exists() ? snap.data().lastHomeVisitAt || null : null;

        if (!cancelled) setPreviousVisitAt(prior);

        // Write this visit's timestamp for next time. Fire-and-forget
        // (logged on failure) so it doesn't block first paint.
        setDoc(userRef, { lastHomeVisitAt: new Date() }, { merge: true }).catch((err) =>
          console.error("useLastVisit write error:", err)
        );
      } catch (err) {
        console.error("useLastVisit error:", err);
        if (!cancelled) setPreviousVisitAt(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();
    return () => { cancelled = true; };
  }, [currentUser]);

  return { previousVisitAt, loading };
}
